import React from 'react'
import { motion } from 'framer-motion'
import { MapPin, Plus, Settings, Trash2, Building } from 'lucide-react'
import { Button } from '@/components'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '@/contexts/LanguageContext'
import miniTurbineIcon from '@/assets/wind_turbine_icon.png'
import type { useTurbineManagementViewModel } from '../useTurbineManagementViewModel'

type VM = ReturnType<typeof useTurbineManagementViewModel>

interface TurbineCardProps {
  turbine: VM['filteredTurbines'][number]
  viewMode: VM['viewMode']
  isSuperAdmin: VM['isSuperAdmin']
  handleEditTurbine: VM['handleEditTurbine']
  handleDeleteTurbine: VM['handleDeleteTurbine']
}

const TurbineCard: React.FC<TurbineCardProps> = ({
  turbine,
  viewMode,
  isSuperAdmin,
  handleEditTurbine,
  handleDeleteTurbine,
}) => {
  const { t } = useLanguage()
  const navigate = useNavigate()

  return (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => navigate(`/dashboard/turbine/${turbine.id}`)}
              className={`bg-white rounded-xl shadow-sm border hover:shadow-lg hover:border-primary-200 hover:scale-105 transition-all duration-300 cursor-pointer ${
                viewMode === 'grid' ? 'p-6' : 'p-4 flex items-center space-x-4'
              }`}
            >
              {viewMode === 'grid' ? (
                <>
                  {/* Grid View - Modern Card Design */}
                  <div className="flex flex-col h-full">
                    {/* Header Section */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start space-x-3 flex-1">
                                                                           <div className="w-14 h-14 flex items-center justify-center flex-shrink-0">
                        <img 
                              src={miniTurbineIcon}
                              alt="Turbine Icon"
                              className="w-12 h-12 object-contain"
                        />
                      </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-extrabold text-gray-900 truncate">
                            {turbine.turbine_name || turbine.name || turbine.code || 'TW' + turbine.id}
                          </h3>
                          <div className="flex items-center space-x-2 mt-1">
                            <p className="text-xs text-gray-500 font-medium">
                              {turbine.city} - {turbine.code}
                            </p>
                          </div>
                    </div>
                      </div>
                  </div>

                    {/* Info Section */}
                    <div className="mb-4 space-y-2">
                      <div className="flex items-start text-sm text-gray-600">
                        <MapPin className="w-4 h-4 mr-2 text-gray-400 mt-0.5 flex-shrink-0" />
                        <span className="text-left leading-relaxed whitespace-pre-wrap break-words">{turbine.location}</span>
                    </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Building className="w-4 h-4 mr-2 text-gray-400" />
                        <span className="truncate">{turbine.company_name}</span>
                    </div>
                  </div>

                    {/* Stats Section */}
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-2.5 rounded-lg border border-blue-200 text-center">
                        <p className="text-lg font-bold text-blue-700">{turbine.rooms}</p>
                        <p className="text-xs text-blue-600">{t.dashboard.turbineRooms}</p>
                      </div>
                      <div className="bg-gradient-to-br from-green-50 to-green-100 p-2.5 rounded-lg border border-green-200 text-center">
                        <p className="text-lg font-bold text-green-700">{turbine.photo_count_360 || 0}</p>
                        <p className="text-xs text-green-600">{t.dashboard.turbinePhotos360}</p>
                      </div>
                      <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-2.5 rounded-lg border border-orange-200 text-center">
                        <p className="text-lg font-bold text-orange-700">{turbine.photo_count_2d || 0}</p>
                        <p className="text-xs text-orange-600">{t.dashboard.turbinePhotos2D}</p>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div
                      className="flex items-center gap-2 mt-auto"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                      {/* Main action buttons */}
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-xs px-3"
                        onClick={() => navigate(`/dashboard/turbine/${turbine.id}`)}
                      >
                        <Plus className="w-3.5 h-3.5 mr-1" />
                        <span className="truncate">{t.dashboard.addPhoto}</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-xs px-3"
                        onClick={() => navigate(`/dashboard/turbine/${turbine.id}/360-view`)}
                      >
                        <span className="truncate">{t.dashboard.view360}</span>
                      </Button>
                      {/* Admin buttons */}
                      {isSuperAdmin && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="p-2 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600"
                            onClick={() => handleEditTurbine(turbine)}
                            title={t.dashboard.edit}
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="p-2 hover:bg-red-50 hover:border-red-200 hover:text-red-600"
                            onClick={() => handleDeleteTurbine(turbine)}
                            title={t.dashboard.delete}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* List View */}
                  <div className="w-14 h-14 flex items-center justify-center flex-shrink-0">
                    <img 
                      src={miniTurbineIcon}
                      alt="Turbine Icon"
                      className="w-12 h-12 object-contain"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div>
                      <h3 className="text-lg font-extrabold text-gray-900">
                        {turbine.turbine_name || turbine.name || turbine.code || `${turbine.city} Türbini`}
                      </h3>
                      <p className="text-xs text-gray-600">{turbine.city} - {turbine.code || 'TW' + turbine.id}</p>
                      <p className="text-sm text-gray-500 mb-2">{turbine.location}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-blue-100 text-blue-700 text-xs font-bold tabular-nums">{turbine.rooms}</span>
                          <span>{t.dashboard.turbineRooms}</span>
                        </span>
                        <span className="text-gray-300">|</span>
                        <span className="flex items-center gap-1">
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-green-100 text-green-700 text-xs font-bold tabular-nums">{turbine.photo_count_360 || 0}</span>
                          <span>{t.dashboard.turbinePhotos360}</span>
                        </span>
                        <span className="text-gray-300">|</span>
                        <span className="flex items-center gap-1">
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-orange-100 text-orange-700 text-xs font-bold tabular-nums">{turbine.photo_count_2d || 0}</span>
                          <span>{t.dashboard.turbinePhotos2D}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div
                    className="flex items-center gap-2"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs px-3"
                      onClick={() => navigate(`/dashboard/turbine/${turbine.id}`)}
                    >
                      {t.dashboard.addPhoto}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs px-3"
                      onClick={() => navigate(`/dashboard/turbine/${turbine.id}/360-view`)}
                    >
                      {t.dashboard.view360}
                    </Button>
                    {isSuperAdmin && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600 p-2"
                          onClick={() => handleEditTurbine(turbine)}
                          title={t.dashboard.edit}
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="hover:bg-red-50 hover:border-red-200 hover:text-red-600 p-2"
                          onClick={() => handleDeleteTurbine(turbine)}
                          title={t.dashboard.delete}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </>
              )}
            </motion.div>
  )
}

export default TurbineCard
